import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { Capacitor } from '@capacitor/core'

import { READONLY_MODE, READWRITE_MODE, ROUTE_NAME } from '@/const'

import { useNotifications } from '@/plugins/notification'

import { prepareResponseError } from '@/store/utils/helpers'

import { useUserStore } from '@/store/user'
import { useUserSubscriptionsStore } from '@/store/user/subscriptions'
import { useSettingsStore } from '@/store/settings'

export async function requireAuth(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  const { warn } = useNotifications()
  const userStore = useUserStore()
  const userSubscriptionsStore = useUserSubscriptionsStore()
  try {
    await userStore.auth()
    if (!userStore.isAuthenticated) {
      throw 'You need to be authorized'
    }
    if (
      userSubscriptionsStore.isInactive &&
      to.name !== ROUTE_NAME.SUBSCRIPTION
    ) {
      next({ name: ROUTE_NAME.SUBSCRIPTION })
      return
    }
    next()
  } catch (e) {
    const message = prepareResponseError(e)
    await warn({ message })
    next({ name: ROUTE_NAME.SIGNIN, query: { path: to.path }, hash: to.hash })
  }
}

export async function isAlreadyAuth(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  const userStore = useUserStore()
  const userSubscriptionsStore = useUserSubscriptionsStore()
  try {
    await userStore.auth()
    if (userStore.isAuthenticated) {
      const entryRoute = userSubscriptionsStore.isInactive
        ? ROUTE_NAME.SUBSCRIPTION
        : ROUTE_NAME.DASHBOARDS
      next({ name: entryRoute })
    } else {
      throw undefined
    }
  } catch {
    next()
  }
}

export async function checkSignupIsAllowed(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  if (!__IS_PROD__ || !!to.query.invite_id) {
    next()
  } else {
    next('/404')
  }
}

export async function redirectToSignin(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  if (
    Capacitor.isNativePlatform() ||
    window.location.origin.includes('//app.')
  ) {
    next({ name: ROUTE_NAME.SIGNIN, query: { path: to.path }, hash: to.hash })
  } else {
    next()
  }
}

export async function deniedIfReadonly(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  // @ts-expect-error - window is a global object
  if (window[READONLY_MODE]) {
    next({ name: ROUTE_NAME.PERMISSION_DENIED })
  } else {
    next()
  }
}

export async function deniedIfReadWrite(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  // @ts-expect-error - window is a global object
  if (window[READWRITE_MODE]) {
    next({ name: ROUTE_NAME.PERMISSION_DENIED })
  } else {
    next()
  }
}

export async function requireAnalyticsDebug(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  const settingsStore = useSettingsStore()
  if (settingsStore.getPowerMode) {
    next()
  } else {
    next('/404')
  }
}
